import { useListingBatch } from "../hooks/useListingBatch";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark } from "lucide-react";
import { fetchEventsByIds } from "../lib/events";
import { useSaved } from "../hooks/useSaved";
import { EventCard } from "../components/EventCard";
import { AdSlot } from "../components/AdSlot";
import { AppMenu } from "../components/AppMenu";

export default function SavedEvents() {
  const navigate = useNavigate();
  const { saved } = useSaved();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch any saved events we don't already hold. `saved` can arrive/refresh
    // after mount (it loads from Supabase), and newly-saved ids may appear — fetch
    // the missing ones so the list fills in. Un-saving on this page still removes
    // cards instantly because the render filters by the live `saved` list below;
    // we never drop already-fetched rows, so un-saved cards persist until reload.
    const haveIds = new Set(all.map((e) => e.id));
    const missing = saved.filter((id) => !haveIds.has(id));
    if (missing.length === 0) {
      setLoading(false);
      return;
    }
    let alive = true;
    fetchEventsByIds(missing)
      .then((rows) => { if (alive) setAll((prev) => [...prev, ...rows]); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const savedEvents = all.filter((ev) => saved.includes(ev.id));

  const { count, sentinel } = useListingBatch(savedEvents.length, saved.join(","));
  return (
    <div data-testid="saved-page" className="min-h-[100dvh] pb-28">
      <div className="sticky top-0 z-30 border-b border-white/10 bg-[#050505] px-5 pb-4 pt-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-extrabold tracking-tight">
            Saved
          </h1>
          <AppMenu />
        </div>
        <p className="mt-1 text-sm text-white/45">
          Your bookmarked events, ready to roll
        </p>
      </div>

      <div className="mx-auto max-w-md px-5 pt-5 md:max-w-7xl md:px-10">
        {loading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] md:gap-5">
            {[0, 1].map((i) => (
              <div key={i} className="h-[240px] animate-pulse rounded-3xl bg-white/5" />
            ))}
          </div>
        ) : savedEvents.length === 0 ? (
          <div
            data-testid="saved-empty"
            className="mt-20 flex flex-col items-center text-center"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-[#121212]">
              <Bookmark className="h-7 w-7 text-white/40" />
            </div>
            <p className="font-display mt-5 text-xl font-bold">
              No saved events yet
            </p>
            <p className="mt-1 max-w-[240px] text-sm text-white/45">
              Tap the bookmark on any event to keep it here.
            </p>
            <button
              data-testid="saved-browse-btn"
              onClick={() => navigate("/")}
              className="mt-6 rounded-full bg-[#ff2a00] px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 active:scale-95"
            >
              Discover events
            </button>
          </div>
        ) : (
          <>
            <p data-testid="saved-count" className="mb-3 text-xs text-white/40">
              {savedEvents.length} saved {savedEvents.length === 1 ? "event" : "events"}
            </p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] md:gap-5">
              {savedEvents.slice(0, count).reduce((acc, ev, i) => {
                acc.push(<EventCard key={ev.id} event={ev} index={i} />);
                if ((i + 1) % 6 === 0) acc.push(<AdSlot key={`ad-${i}`} page="saved" />);
                return acc;
              }, [])}
            </div>
            {count < savedEvents.length && <div ref={sentinel} className="h-4" />}
          </>
        )}
      </div>
    </div>
  );
}
